import { queryClient } from "@istari-frontend/api-client";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { extractBackendError } from "~/ai-chat/data-source/backend-error";
import { setConversationArchived } from "~/ai-chat/data-source/conversations-api";
import { clearLlmApiKey, getLlmApiKey, openLlmKeyPrompt } from "~/ai-chat/data-source/llm-api-key-store";
import { streamChatMessage } from "~/ai-chat/data-source/stream-chat-message";
import { conversationKeys, refreshAfterTurn, useCreateConversation } from "~/ai-chat/data-source/use-conversations";
import { createAssistantBubble } from "~/ai-chat/feature/assistant-bubble";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { useChatContext } from "~/ai-chat/feature/use-chat-context";
import { useChatViewSnapshot } from "~/ai-chat/feature/use-chat-view-context";
import { serializeChatContext } from "~/ai-chat/model/chat-context";
import { chatErrorCodeForStatus, chatErrorMessage } from "~/ai-chat/model/chat-error-copy";
import type { ChatMessage, ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import { createDeltaJoiner } from "~/ai-chat/model/delta-joiner";
import { ENTITY_LINK_INSTRUCTIONS } from "~/ai-chat/model/entity-link";
import { SAVE_RESOURCE_INSTRUCTIONS } from "~/ai-chat/model/save-resource-action";
import { toolActivityLabel } from "~/ai-chat/model/tool-activity";

interface ChatSendApi {
  // `history` overrides the prior transcript sent to the backend (an edited turn
  // truncates it there); it defaults to the current transcript.
  send: (content: string, history?: ChatTranscriptItem[]) => Promise<void>;
  // Abort the in-flight stream, keeping whatever already streamed. No-op when idle.
  stop: () => void;
  // Re-send a user turn (error-bubble Retry). Defaults to the most recent turn
  // when no content is given. No-op when nothing has been sent yet.
  retry: (content?: string) => void;
  // Approve a paused gated call: re-open the stream with the approval id so the server runs it and
  // streams the continuation. Rejects on an open failure (expired / already answered) so the card
  // can show it. Decline is not here — it POSTs to the approvals endpoint (resolve-approval).
  resume: (approvalId: string) => Promise<void>;
  isPending: boolean;
  // A short "Using <tool>…" label while the agent is running a tool, or null.
  activity: string | null;
}

function onlyMessages(items: ChatTranscriptItem[]): ChatMessage[] {
  return items.filter((item): item is ChatMessage => item.kind === "message");
}

// Derive a conversation title from the first user message (first six words),
// mirroring the window title so a saved chat is recognizable in the sidebar.
function deriveConversationTitle(content: string): string {
  const words = content.trim().split(/\s+/).slice(0, 6).join(" ");
  return words.length > 60 ? `${words.slice(0, 60).trimEnd()}…` : words;
}

export function useChatSend(): ChatSendApi {
  const {
    messages,
    appendMessage,
    updateMessage,
    removeMessage,
    markUnavailable,
    markMcpToolsAvailable,
    activeConversationId,
    bindConversationId,
    markConversationUnsynced,
    markConversationSynced,
    runningConversationIds,
    markConversationRunning,
    markConversationIdle,
    getConversationEpoch,
    setRestoredDraft,
    chatTitle,
    setChatTitle,
    selectedModelId,
    notebookContext,
    chatControllersRef: controllersRef,
    newChatControllerRef,
    activeConversationIdRef,
    newChatPending,
    setNewChatPending,
    setTabOpenIds,
  } = useAiChat();
  const chatContext = useChatContext();
  const snapshotViewContext = useChatViewSnapshot();
  const { mutateAsync: createConversation } = useCreateConversation();
  // Multiple conversations can stream at once (F14): a send never aborts another
  // conversation's run. `runningConversationIds` (shared via context, so the status
  // dots reflect it) tracks saved conversations with a live stream; `newChatPending`
  // covers the brief window before a brand-new chat has an id. The composer is
  // "pending" only for the conversation currently on screen.
  const [activity, setActivity] = useState<string | null>(null);
  const isPending = activeConversationId === null ? newChatPending : runningConversationIds.has(activeConversationId);

  // `activeConversationIdRef` is provider-owned (see useAiChat): a background run tests it
  // to decide whether its tokens may touch the shared transcript, and it must keep updating
  // even after this hook remounts (pop-out/dock) so a later tab switch can't be painted over.
  // A tool-activity label belongs to the on-screen run; reset it on every switch.
  useEffect(() => setActivity(null), [activeConversationId]);

  // One abort controller per streaming conversation (plus the not-yet-created chat) —
  // held in the provider (see useAiChat) so a ChatPanel remount (popout/dock) neither
  // aborts nor loses them; only the chat feature fully unmounting aborts them there.
  const clearRun = useCallback(
    (conversationId: string | null, controller: AbortController) => {
      if (conversationId !== null && controllersRef.current.get(conversationId) === controller) {
        controllersRef.current.delete(conversationId);
        markConversationIdle(conversationId);
      }
      if (newChatControllerRef.current === controller) newChatControllerRef.current = null;
    },
    [markConversationIdle, controllersRef, newChatControllerRef],
  );

  // User-triggered stop targets the on-screen conversation's stream. The send loop's
  // catch treats an aborted signal as a non-error, so the partial reply is kept.
  const stop = useCallback(() => {
    const id = activeConversationIdRef.current;
    if (id !== null) controllersRef.current.get(id)?.abort();
    else newChatControllerRef.current?.abort();
  }, [controllersRef, newChatControllerRef, activeConversationIdRef]);

  // The last user turn's content, so a failed/finished turn can be re-sent
  // verbatim (Retry / Regenerate) even after the optimistic message rolled back.
  const lastUserContentRef = useRef<string | null>(null);

  const send = useCallback(
    async (content: string, history: ChatTranscriptItem[] = messages) => {
      const trimmed = content.trim();
      // Blocks only a duplicate send of the SAME on-screen conversation; a different
      // (or brand-new) chat may start while this one is still streaming.
      if (trimmed.length === 0 || isPending) return;
      lastUserContentRef.current = trimmed;

      const userMessage: ChatMessage = {
        kind: "message",
        id: crypto.randomUUID(),
        role: "user",
        content: trimmed,
        createdAt: new Date(),
      };
      const conversation = [...onlyMessages(history), userMessage];
      const epochAtSend = getConversationEpoch();
      const startedActiveId = activeConversationId;
      appendMessage(userMessage);
      setActivity(null);

      const controller = new AbortController();
      if (startedActiveId !== null) {
        // Re-send / edit of an existing chat replaces its in-flight turn — abort only
        // that conversation's stream, never the others.
        controllersRef.current.get(startedActiveId)?.abort();
        controllersRef.current.set(startedActiveId, controller);
        markConversationRunning(startedActiveId);
      } else {
        newChatControllerRef.current = controller;
        setNewChatPending(true);
      }

      // Ensure a conversation id up front so the backend can persist this turn
      // server-side (F14 R3): the id is passed to the stream, which appends the
      // user turn (+ `running`) before streaming and the assistant turn (+ terminal
      // status) in its `finally`. Create on the first send; a create failure just
      // means no server persistence this turn, never a broken chat.
      let conversationId = startedActiveId;
      let createdConversationId: string | null = null;
      if (conversationId === null) {
        try {
          const trimmedTitle = chatTitle?.trim();
          const created = await createConversation(
            trimmedTitle && trimmedTitle.length > 0 ? trimmedTitle : deriveConversationTitle(trimmed),
          );
          conversationId = created.id;
          createdConversationId = created.id;
          // Move this run's bookkeeping from the unsaved slot onto the real id.
          controllersRef.current.set(created.id, controller);
          markConversationRunning(created.id);
          newChatControllerRef.current = null;
          setNewChatPending(false);
          // Publish the id only if the user hasn't navigated while the create was
          // in flight; the turn persists server-side under it either way. Unsynced
          // before publishing so the sidebar won't hydrate over the live reply.
          if (getConversationEpoch() === epochAtSend) {
            markConversationUnsynced(created.id);
            // bindConversationId syncs activeConversationIdRef eagerly, so this run's
            // first deltas immediately recognise their conversation as the one on screen.
            bindConversationId(created.id);
            setChatTitle(created.title);
          }
        } catch {
          // Couldn't create: stream without an id — the server won't persist this
          // turn, but the live chat still works (v0 behaviour). Clear the pending flag
          // so Save/Share (gated on it) don't stay disabled.
          setNewChatPending(false);
        }
      } else if (getConversationEpoch() === epochAtSend) {
        markConversationUnsynced(conversationId);
      }

      // True while this run's conversation is the one on screen — only then may its
      // tokens/errors touch the shared transcript. A backgrounded run keeps streaming
      // (and persisting server-side) but paints nothing.
      const isViewing = () => activeConversationIdRef.current === conversationId;

      const bubble = createAssistantBubble(appendMessage, updateMessage);
      // Accumulate the assistant reply for the optimistic bubble (the raw turn is
      // persisted server-side now, so this is display-only).
      let assistantContent = "";
      const deltaJoiner = createDeltaJoiner();
      // The stream can deliver a terminal `error` event; track it so the catch
      // block below doesn't also append a duplicate error bubble.
      let sawStreamError = false;
      // Approval cards appended this run. If the run ends (done / error / stop /
      // timeout) while any is still `pending`, the paused turn can no longer use a
      // decision — settle those to `expired` so their buttons don't linger as live.
      const approvalMessageIds: string[] = [];
      // Non-blocking model: the stream ENDS on an approval_request (the turn is paused server-side,
      // durable with a TTL). The card must stay actionable, so when the run ends on a pause we do
      // NOT expire its card — the user can still approve (resume) or deny.
      let endedOnApprovalPause = false;
      const settlePendingApprovals = () => {
        for (const id of approvalMessageIds) {
          updateMessage(id, (item) =>
            item.kind === "approval" && item.status === "pending" ? { ...item, status: "expired" } : item,
          );
        }
      };

      // A send that failed before producing any reply: roll back the optimistic
      // user message (or the next send would carry the same turn twice) and hand
      // the text back to the composer.
      const rollbackFailedSend = () => {
        if (assistantContent.length > 0) return;
        removeMessage(userMessage.id);
        if (isViewing()) setRestoredDraft(trimmed);
      };

      // Classify a stream-open failure and surface it to the on-screen chat only:
      // a rejected model / unconfigured server toast, or an inline error bubble.
      const surfaceOpenError = async (error: unknown) => {
        const { status, detail } = await extractBackendError(error);
        // A chosen model the server rejects (404 unknown / 400 not-offered) names its model in the
        // detail. Gate on that so a bare route 404 (AI not configured) and unrelated 400s fall
        // through to their own handling instead of all masquerading as "pick another model".
        const modelRejected =
          selectedModelId !== null && (status === 404 || status === 400) && /\bmodel\b/i.test(detail ?? "");
        if (modelRejected) {
          if (isViewing()) toast.error("That model is unavailable. Pick another.", { duration: 8000 });
          return;
        }
        if (status === 404) {
          markUnavailable();
          if (isViewing()) toast.error("Istari AI is not configured on this server.", { duration: 8000 });
          return;
        }
        if (sawStreamError || !isViewing()) return;
        const code = chatErrorCodeForStatus(status);
        appendMessage({
          kind: "error",
          id: crypto.randomUUID(),
          content: chatErrorMessage(code, detail),
          userContent: trimmed,
          ...(code ? { code } : {}),
        });
      };

      // A settled turn (done / error / aborted) is persisted server-side; release the
      // unsynced guard and refetch what the server just wrote (messages + sidebar list).
      const markTurnSettled = () => {
        if (conversationId === null) return;
        markConversationSynced(conversationId);
        refreshAfterTurn(conversationId);
      };

      try {
        const systemPrompt = [
          serializeChatContext(chatContext, snapshotViewContext()),
          notebookContext,
          ENTITY_LINK_INSTRUCTIONS,
          SAVE_RESOURCE_INSTRUCTIONS,
        ]
          .filter((part): part is string => part !== null)
          .join("\n\n");
        await streamChatMessage({
          messages: conversation,
          systemPrompt,
          llmApiKey: getLlmApiKey(),
          modelPk: selectedModelId,
          conversationId,
          signal: controller.signal,
          onEvent: (event) => {
            switch (event.type) {
              case "delta": {
                const text = deltaJoiner.next(event.content);
                assistantContent += text;
                // Backgrounded run: it persists server-side, but its tokens must not
                // paint the chat currently on screen.
                if (!isViewing()) break;
                bubble.appendDelta(text);
                setActivity(null);
                break;
              }
              case "toolCall": {
                deltaJoiner.toolCalled();
                if (!isViewing()) break;
                setActivity(event.status === "started" ? toolActivityLabel(event.name) : null);
                bubble.recordToolCall(event);
                break;
              }
              case "approvalRequest": {
                // A gated call needs the user's go-ahead. Append an approval card; the
                // turn is paused server-side on the open stream until the user answers
                // (or it times out, fail-closed). Only painted when on screen — a
                // backgrounded run cannot prompt, so it will time out server-side.
                endedOnApprovalPause = true;
                if (!isViewing()) break;
                bubble.dropGatedToolCall(event.tool);
                bubble.flush();
                const approvalMsgId = crypto.randomUUID();
                approvalMessageIds.push(approvalMsgId);
                appendMessage({
                  kind: "approval",
                  id: approvalMsgId,
                  approvalId: event.approvalId,
                  tool: event.tool,
                  message: event.message,
                  status: "pending",
                });
                setActivity(null);
                break;
              }
              case "done": {
                if (event.mcpServerUrl !== null) markMcpToolsAvailable();
                break;
              }
              case "error": {
                sawStreamError = true;
                rollbackFailedSend();
                // The user's own LLM key was rejected: drop it, tell them, and
                // re-open the key prompt so they can re-enter and retry. This is
                // distinct from agent_failed / circular_tool_calls, which surface
                // as an inline error bubble.
                if (event.code === "token_expired") {
                  clearLlmApiKey();
                  if (isViewing()) {
                    toast.error("Your LLM key was rejected. Please re-enter it.", { duration: 8000 });
                    openLlmKeyPrompt();
                  }
                  break;
                }
                if (isViewing()) {
                  appendMessage({
                    kind: "error",
                    id: crypto.randomUUID(),
                    content: chatErrorMessage(event.code, event.message),
                    userContent: trimmed,
                    code: event.code,
                    requestId: event.requestId,
                  });
                }
                break;
              }
            }
          },
        });
      } catch (error) {
        // User Stop (or chat closed): keep whatever streamed. The backend marks the
        // turn `aborted` and persists the partial in its own `finally`.
        if (controller.signal.aborted) {
          return;
        }
        // Pre-stream failure: the request never opened, so nothing was persisted.
        // Roll back + surface the error first — both gate on `isViewing()`, which still
        // sees the created conversation as the one on screen at this point.
        rollbackFailedSend();
        await surfaceOpenError(error);
        // A conversation created for this send is now an empty orphan — archive it
        // (best-effort; there is no hard delete), then refresh the list to hide it.
        if (createdConversationId !== null) {
          // Un-publish the archived orphan's id (only after the error is on screen, so
          // `isViewing()` stayed true above); otherwise a retry reuses the archived id
          // and lands hidden in the archived bucket instead of a fresh conversation.
          if (activeConversationIdRef.current === createdConversationId) {
            bindConversationId(null);
            setChatTitle(chatTitle);
          }
          setTabOpenIds((prev) => prev.filter((id) => id !== createdConversationId));
          void setConversationArchived(createdConversationId, true)
            .then(() => queryClient.invalidateQueries({ queryKey: conversationKeys.lists() }))
            .catch(() => {});
        }
      } finally {
        // Every terminal path (done / error event / abort / pre-stream failure) is resolved
        // server-side; a still-pending approval card can no longer be acted on, so settle it —
        // EXCEPT when the run ended on an approval pause, where the card stays live (the user
        // approves via resume or denies).
        if (!endedOnApprovalPause) settlePendingApprovals();
        // release the unsynced guard + refetch the persisted turn, then
        // drop this run from the running set so the composer un-pends for its chat.
        markTurnSettled();
        clearRun(conversationId, controller);
        if (conversationId === null) setNewChatPending(false);
        // A turn that errors with no text is rolled back whole (see rollbackFailedSend), so it keeps no calls.
        if (isViewing() && !sawStreamError) bubble.flush();
        if (isViewing()) setActivity(null);
      }
    },
    [
      messages,
      appendMessage,
      updateMessage,
      removeMessage,
      markUnavailable,
      markMcpToolsAvailable,
      activeConversationId,
      bindConversationId,
      markConversationUnsynced,
      markConversationSynced,
      getConversationEpoch,
      setRestoredDraft,
      chatTitle,
      setChatTitle,
      selectedModelId,
      chatContext,
      snapshotViewContext,
      createConversation,
      notebookContext,
      isPending,
      clearRun,
      markConversationRunning,
      controllersRef,
      newChatControllerRef,
      activeConversationIdRef,
      setNewChatPending,
      setTabOpenIds,
    ],
  );

  // Resume a turn that paused on an approval the user just APPROVED (non-blocking model). Unlike
  // `send` there is no new user message and the conversation already exists: re-open the stream with
  // `resumeApprovalId`, and the server re-executes the gated call and streams the continuation, which
  // we append as a normal assistant reply. Rejects on an open failure (e.g. the approval expired /
  // was already answered → 400) so the card can show the failure; a DECLINE does not come here (it
  // POSTs to the approvals endpoint). Reuses the same controller/running bookkeeping as `send`.
  const resume = useCallback(
    async (approvalId: string): Promise<void> => {
      const conversationId = activeConversationIdRef.current;
      if (conversationId === null) throw new Error("Cannot resume an approval without a conversation.");
      // Already streaming this conversation (double-approve / a concurrent send): reject so the card
      // doesn't stay stuck on "Approved — running…".
      if (runningConversationIds.has(conversationId)) {
        throw new Error("A resume is already in progress for this conversation.");
      }

      const conversation = onlyMessages(messages);
      const epochAtSend = getConversationEpoch();
      const controller = new AbortController();
      controllersRef.current.get(conversationId)?.abort();
      controllersRef.current.set(conversationId, controller);
      markConversationRunning(conversationId);
      if (getConversationEpoch() === epochAtSend) markConversationUnsynced(conversationId);
      setActivity(null);

      const isViewing = () => activeConversationIdRef.current === conversationId;
      const bubble = createAssistantBubble(appendMessage, updateMessage);
      const deltaJoiner = createDeltaJoiner();
      const approvalMessageIds: string[] = [];
      let endedOnApprovalPause = false;
      // A terminal stream error still resolves streamChatMessage; track it so resume can reject
      // afterwards (the card flips to failed) while the inline error bubble stays.
      let sawStreamError = false;
      const settlePendingApprovals = () => {
        for (const id of approvalMessageIds) {
          updateMessage(id, (item) =>
            item.kind === "approval" && item.status === "pending" ? { ...item, status: "expired" } : item,
          );
        }
      };

      try {
        const systemPrompt = [
          serializeChatContext(chatContext, snapshotViewContext()),
          notebookContext,
          ENTITY_LINK_INSTRUCTIONS,
          SAVE_RESOURCE_INSTRUCTIONS,
        ]
          .filter((part): part is string => part !== null)
          .join("\n\n");
        await streamChatMessage({
          messages: conversation,
          systemPrompt,
          llmApiKey: getLlmApiKey(),
          modelPk: selectedModelId,
          conversationId,
          resumeApprovalId: approvalId,
          signal: controller.signal,
          onEvent: (event) => {
            switch (event.type) {
              case "delta": {
                const text = deltaJoiner.next(event.content);
                if (!isViewing()) break;
                bubble.appendDelta(text);
                setActivity(null);
                break;
              }
              case "toolCall": {
                deltaJoiner.toolCalled();
                if (!isViewing()) break;
                setActivity(event.status === "started" ? toolActivityLabel(event.name) : null);
                bubble.recordToolCall(event);
                break;
              }
              case "approvalRequest": {
                // The continuation hit ANOTHER gated call — pause again with a fresh card.
                endedOnApprovalPause = true;
                if (!isViewing()) break;
                bubble.dropGatedToolCall(event.tool);
                bubble.flush();
                const approvalMsgId = crypto.randomUUID();
                approvalMessageIds.push(approvalMsgId);
                appendMessage({
                  kind: "approval",
                  id: approvalMsgId,
                  approvalId: event.approvalId,
                  tool: event.tool,
                  message: event.message,
                  status: "pending",
                });
                setActivity(null);
                break;
              }
              case "done": {
                if (event.mcpServerUrl !== null) markMcpToolsAvailable();
                break;
              }
              case "error": {
                // Mid-stream failure after the resume started: surface an inline bubble (the gated
                // call may already have run — don't claim otherwise), and mark the resume failed.
                sawStreamError = true;
                if (event.code === "token_expired") {
                  clearLlmApiKey();
                  if (isViewing()) {
                    toast.error("Your LLM key was rejected. Please re-enter it.", { duration: 8000 });
                    openLlmKeyPrompt();
                  }
                  break;
                }
                if (isViewing()) {
                  appendMessage({
                    kind: "error",
                    id: crypto.randomUUID(),
                    content: chatErrorMessage(event.code, event.message),
                    userContent: "",
                    code: event.code,
                    requestId: event.requestId,
                  });
                }
                break;
              }
            }
          },
        });
        // Stream ended in an error: reject so the card flips to failed (the bubble above explains it).
        if (sawStreamError) throw new Error("The resume stream ended in an error.");
        // No catch: every failure — open error (expired / already answered), user abort, or a stream
        // error — propagates to the card so it never stays stuck on "Approved — running…". The finally
        // still runs cleanup on every path.
      } finally {
        if (!endedOnApprovalPause) settlePendingApprovals();
        if (conversationId !== null) {
          markConversationSynced(conversationId);
          refreshAfterTurn(conversationId);
        }
        clearRun(conversationId, controller);
        if (isViewing() && !sawStreamError) bubble.flush();
        if (isViewing()) setActivity(null);
      }
    },
    [
      messages,
      appendMessage,
      updateMessage,
      markMcpToolsAvailable,
      markConversationRunning,
      markConversationUnsynced,
      markConversationSynced,
      runningConversationIds,
      getConversationEpoch,
      selectedModelId,
      chatContext,
      snapshotViewContext,
      notebookContext,
      clearRun,
      controllersRef,
      activeConversationIdRef,
    ],
  );

  const retry = useCallback(
    (content?: string) => {
      const target = content ?? lastUserContentRef.current;
      if (target !== null) void send(target);
    },
    [send],
  );

  return { send, stop, retry, resume, isPending, activity };
}
